import { UserType } from "../recoil/userAtom";
import { getDaysSince } from "../logic/logics_common";

export type RefundInitialDecisionKey =
  | "NO_PAYMENT"
  | "NOT_FIRST"
  | "ALREADY_REFUNDED"
  | "PERIOD_OVER"
  | "MANDATORY_NOT_DONE"
  | "EMAIL";

export type RefundExtendedDecisionKey = "NO_PAYMENT" | "NOT_EXTENDED" | "CHANNEL_TALK";

type DecisionResult<K extends string> = {
  key: K;
  title: string;
  subtitle: string;
};

export function decideInitialRefund({
  user,
  limitDays = 14,
}: {
  user?: UserType;
  limitDays?: number;
}): DecisionResult<RefundInitialDecisionKey> {
  const isSubscribing = !!user?.subscription?.isSubscribing;
  const isFirstSubscription = user?.subscription?.isFirstSubscription || false;
  const hasBeenRefunded = !!user?.subscription?.hasBeenRefunded;
  const isMandatoryTaskForRefundDone =
    user?.isMandatoryTaskForRefundDone || false;
  const startedAtStr = user?.programStartedAt as string | undefined;

  if (!isSubscribing) {
    return {
      key: "NO_PAYMENT",
      title: "환불할 결제가 없어요.",
      subtitle: "현재 환불을 신청할 결제 내역이 존재하지 않아요.",
    } as const;
  } else if (!isFirstSubscription) {
    return {
      key: "NOT_FIRST",
      title: "최초 이용 중이 아니에요",
      subtitle: "연장 이용에 대한 환불 규정을 확인하신 후 다시 시도해주세요.",
    } as const;
  } else if (hasBeenRefunded) {
    return {
      key: "ALREADY_REFUNDED",
      title: "이미 환불받은 적이 있어요",
      subtitle: "환불 이력이 있어 더 이상 환불 보장이 적용되지 않습니다.",
    } as const;
  } else if (
    startedAtStr !== undefined &&
    getDaysSince(startedAtStr!!) >= limitDays
  ) {
    return {
      key: "PERIOD_OVER",
      title: "환불 기간이 지났어요",
      subtitle: "결제일로부터 14일 이내에만 환불이 가능해요.",
    } as const;
  } else if (!isMandatoryTaskForRefundDone) {
    return {
      key: "MANDATORY_NOT_DONE",
      title: "조금 더 해보세요",
      subtitle:
        "2세트 이상 제공된 활동지를 진행했음에도 불만족하신 경우 환불이 가능합니다. 홈 화면에 주어지는 활동지 전체가 1세트입니다.",
    } as const;
  }

  return {
    key: "EMAIL",
    title: "메일로 접수해주세요",
    subtitle:
      "policy@orwellhealth.org로 닉네임과 회원번호를 포함하여 환불 신청서를 보내주세요.",
  } as const;
}

/**
 * Decide the modal message for Extended (renewal) refund policy.
 * Mirrors the logic used in ExtendedRefundLink.
 */
export function decideExtendedRefund(
  user: any
): DecisionResult<RefundExtendedDecisionKey> {
  const isSubscribing = !!user?.subscription?.isSubscribing;
  const isFirstSubscription = user?.subscription?.isFirstSubscription as
    | boolean
    | undefined;

  if (!isSubscribing) {
    return {
      key: "NO_PAYMENT",
      title: "환불할 결제가 없어요.",
      subtitle: "현재 환불을 신청할 결제 내역이 존재하지 않아요.",
    } as const;
  } else if (isFirstSubscription === true) {
    return {
      key: "NOT_EXTENDED",
      title: "연장 이용 중이 아니에요",
      subtitle: "최초 이용에 대한 환불 규정을 확인하신 후 다시 시도해주세요.",
    } as const;
  }

  return {
    key: "CHANNEL_TALK",
    title: "고객센터로 접수해 주세요",
    subtitle: "고객센터로 연장 이용 환불 신청서를 보내주세요.",
  } as const;
}
